// src/services/api.js
// Updated: Added Analytics API endpoints
// Includes all dashboard, sales, products, customers, inventory, reviews, promos, and support analytics

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';


const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Helper to extract pure base64 from data URL (e.g., strip "data:image/jpeg;base64,")
const getPureBase64 = (dataUrlOrString) => {
  if (!dataUrlOrString || typeof dataUrlOrString !== 'string') return null;
  if (dataUrlOrString.startsWith('http')) return dataUrlOrString; // Keep existing URLs as-is
  const matches = dataUrlOrString.match(/^data:[^;]+;base64,(.+)$/);
  return matches ? matches[1] : null;
};

// Helper to process images for payload
const processImagePayload = (productData) => {
  const processed = { ...productData };

  // Process main image
  if (processed.image) {
    processed.image = getPureBase64(processed.image);
    if (!processed.image) {
      delete processed.image; // Remove invalid
    }
  }

  // Process gallery images (array, mix of URLs and data URLs)
  if (processed.galleryImages && Array.isArray(processed.galleryImages)) {
    processed.galleryImages = processed.galleryImages
      .map(img => getPureBase64(img))
      .filter(img => img !== null); // Remove invalid
  }

  return processed;
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  getUsers: () => api.get('/auth/users'),
  getUserById: (id) => api.get(`/auth/users/${id}`),
  updateUser: (id, userData) => api.put(`/auth/users/${id}`, userData),
  changePassword: (id, passwordData) => api.put(`/auth/users/${id}/password`, passwordData),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (profileData) => api.put('/auth/profile', profileData),
};

export const productsAPI = {
  getAll: (categoryId = null) => {
    const params = categoryId ? { categoryId } : {};
    return api.get('/products', { params });
  },
  getById: (id) => api.get(`/products/${id}`),
  getBySlug: (slug) => api.get(`/products/slug/${slug}`),
  create: (productData) => {
    const processedData = processImagePayload(productData);
    return api.post('/products', processedData);
  },
  generateDescriptions: (payload) => api.post('/products/generate/descriptions', payload),
  update: (id, productData) => {
    const processedData = processImagePayload(productData);
    return api.put(`/products/${id}`, processedData);
  },
  delete: (id) => api.delete(`/products/${id}`),
  getLowStock: () => api.get('/products/low-stock'),
  search: (query) => api.get(`/search?q=${encodeURIComponent(query)}`),
  getReviews: (productId, page = 1) =>
    api.get(`/products/${productId}/reviews`, { params: { page, limit: 5 } }),
  submitReview: (productId, data) =>
    api.post(`/products/${productId}/review`, data),
  deleteReview: (productId, reviewId) =>
    api.delete(`/products/${productId}/review/${reviewId}`),
  getAdminReviews: (params = {}) => api.get('/products/admin/reviews', { params }),
  getTrash: () => api.get('/products/trash'),
  restore: (id) => api.post(`/products/${id}/restore`),
  purge: (id) => api.delete(`/products/${id}/purge`),
};

export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  getById: (id) => api.get(`/categories/${id}`),
  getBySlug: (slug) => api.get(`/categories/slug/${slug}`),
  create: (categoryData) => api.post('/categories', categoryData),
  update: (id, categoryData) => api.put(`/categories/${id}`, categoryData),
  delete: (id) => api.delete(`/categories/${id}`),
};

export const cartAPI = {
  addItem: (itemData) => api.post('/cart', itemData),
  getCart: () => api.get('/cart'),
  updateQuantity: (data) => api.put('/cart', data),
  deleteItem: (productId) => api.delete(`/cart/${productId}`),
  clearCart: () => api.delete('/cart'),
};

export const ordersAPI = {
  placeOrder: (orderData) => api.post('/orders', orderData),
  getAll: () => api.get('/orders'),
  getOrdersByUser: (userId) => api.get(`/orders/user/${userId}`),
  getMyOrders: () => api.get('/orders/my-orders'),
  updateStatus: (orderId, status) => api.put(`/orders/${orderId}`, { status }),
  getById: (id) => api.get(`/orders/${id}`),
  getUnviewed: () => api.get('/orders/unviewed'),
  markNotificationViewed: (id) => api.put(`/orders/${id}/notification`),
  getOrderByTrackingCode: (code) => api.get(`/orders/track/${code}`),
};

export const promosAPI = {
  apply: (data) => api.post('/promos/apply', data),
  getAll: () => api.get('/promos'),
  create: (promoData) => api.post('/promos', promoData),
  update: (id, promoData) => api.put(`/promos/${id}`, promoData),
  delete: (id) => api.delete(`/promos/${id}`),
  getStats: (id) => api.get(`/promos/${id}/stats`),
};

export const deliveryAPI = {
  getSettings: () => api.get('/delivery/settings'),
  updateSettings: (settings) => api.put('/delivery/settings', settings),
};

export const themeAPI = {
  getBanners: () => api.get('/theme/banners'),
  createBanner: (bannerData) => api.post('/theme/banners', bannerData),
  updateBanner: (id, bannerData) => api.put(`/theme/banners/${id}`, bannerData),
  deleteBanner: (id) => api.delete(`/theme/banners/${id}`),
};

export const chatAPI = {
  startConversation: (data = {}) => {
    const guestToken = localStorage.getItem('chat_guest_token');
    const payload = { ...data };
    
    if (guestToken && !localStorage.getItem('token')) {
      payload.guestToken = guestToken;
    }
    
    return api.post('/chat/start', payload);
  },

  getConversation: (convoId, customHeaders = {}) => {
    const guestToken = localStorage.getItem('chat_guest_token');
    const headers = { ...customHeaders };
    
    if (guestToken && !localStorage.getItem('token')) {
      headers['x-guest-token'] = guestToken;
    }
    
    return api.get(`/chat/${convoId}`, { headers });
  },

  getConversationStatus: (convoId, customHeaders = {}) => {
    const guestToken = localStorage.getItem('chat_guest_token');
    const headers = { ...customHeaders };
    
    if (guestToken && !localStorage.getItem('token')) {
      headers['x-guest-token'] = guestToken;
    }
    
    return api.get(`/chat/${convoId}/status`, { headers });
  },

  migrateGuest: (data = {}) => {
    const guestToken = localStorage.getItem('chat_guest_token');
    const payload = { ...data };
    
    if (guestToken) {
      payload.guestToken = guestToken;
    }
    
    return api.post('/chat/migrate-guest', payload);
  },

  getUserConversations: () => api.get('/chat/user/conversations'),

  getActiveConversations: () => api.get('/chat/admin/active'),
  claimConversation: (convoId) => api.post(`/chat/admin/claim/${convoId}`),
  closeConversation: (convoId) => api.post(`/chat/admin/close/${convoId}`),
  reopenConversation: (convoId) => api.post(`/chat/admin/reopen/${convoId}`),
};

export const aiSearchAPI = {
  search: (query, context = {}) => api.post('/ai-search/search', { 
    query,
    ...context 
  }),
  health: () => api.get('/ai-search/health'),
  getSuggestions: (query) => api.get('/ai-search/suggestions', { 
    params: { q: query } 
  }),
};

// ============================================
// ANALYTICS API - Complete Analytics Suite
// ============================================
export const analyticsAPI = {
  // Dashboard Overview - Main metrics summary
  getDashboard: (period = 'month') => 
    api.get('/analytics/dashboard', { params: { period } }),

  // Sales Analytics - Detailed sales insights
  getSales: (period = 'month', groupBy = 'day') => 
    api.get('/analytics/sales', { params: { period, groupBy } }),

  // Product Analytics - Product performance metrics
  getProducts: (period = 'month', categoryId = null) => 
    api.get('/analytics/products', { params: { period, categoryId } }),

  // Customer Analytics - Customer insights and segmentation
  getCustomers: (period = 'month') => 
    api.get('/analytics/customers', { params: { period } }),

  // Inventory Analytics - Stock management insights
  getInventory: () => 
    api.get('/analytics/inventory'),

  // Review Analytics - Review metrics and ratings
  getReviews: (period = 'month') => 
    api.get('/analytics/reviews', { params: { period } }),

  // Promo Analytics - Promotion performance
  getPromos: (period = 'month') => 
    api.get('/analytics/promos', { params: { period } }),

  // Support Analytics - Chat support metrics
  getSupport: (period = 'month') => 
    api.get('/analytics/support', { params: { period } }),

  // Export Data - CSV export for reports
  exportData: (type, period = 'month') => 
    api.get(`/analytics/export/${type}`, { 
      params: { period },
      responseType: 'blob'
    }),
};

export const seoAPI = {
  getProductMeta: (productId) => api.get(`/seo/meta/product/${productId}`),
  getProductMetaBySlug: (slug) => api.get(`/seo/meta/product/slug/${slug}`),
  getCategoryMeta: (categoryId) => api.get(`/seo/meta/category/${categoryId}`),
  getCategoryMetaBySlug: (slug) => api.get(`/seo/meta/category/slug/${slug}`),
  getSitemap: () => api.get('/seo/sitemap.xml'),
  getRobots: () => api.get('/seo/robots.txt'),
};

export default api;
